/**
 * Геометрия дуги большого круга: расстояния, курс, точка на маршруте.
 * Клиент рисует по этим же функциям, что и сервер считает - иначе самолёт
 * летит не там, где начисляются деньги.
 */

export interface LatLon {
  lat: number;
  lon: number;
}

/** Средний радиус Земли по IUGG, км. Тот же радиус обязан использовать клиент. */
export const EARTH_RADIUS_KM = 6371.0088;

const DEG = Math.PI / 180;
const RAD = 180 / Math.PI;

function toRad(deg: number): number {
  return deg * DEG;
}

/**
 * Приводит долготу к диапазону (-180, 180].
 * Значение, уже лежащее в диапазоне, возвращается БЕЗ арифметики: прогон через
 * остаток от деления теряет младшие биты (-0.4543 превращается в -0.45429999999998927),
 * а клиент и сервер обязаны получать побитово одинаковые координаты.
 */
function normalizeLon(lon: number): number {
  if (lon > -180 && lon <= 180) return lon;
  let x = ((lon + 180) % 360) - 180;
  if (x <= -180) x += 360;
  return x;
}

/** Расстояние по дуге большого круга, км. */
export function haversineKm(a: LatLon, b: LatLon): number {
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const sinDLat = Math.sin(dLat / 2);
  const sinDLon = Math.sin(dLon / 2);
  const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLon * sinDLon;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Начальный курс из a в b, градусы 0..360 (0 - север). */
export function initialBearingDeg(a: LatLon, b: LatLon): number {
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const dLon = toRad(b.lon - a.lon);
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  return (Math.atan2(y, x) * RAD + 360) % 360;
}

/** Точка на расстоянии distKm от start по курсу bearingDeg. */
export function destinationPoint(start: LatLon, bearingDeg: number, distKm: number): LatLon {
  const d = distKm / EARTH_RADIUS_KM;
  const brg = toRad(bearingDeg);
  const lat1 = toRad(start.lat);
  const lon1 = toRad(start.lon);
  const sinLat2 = Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(brg);
  const lat2 = Math.asin(Math.min(1, Math.max(-1, sinLat2)));
  const lon2 =
    lon1 +
    Math.atan2(
      Math.sin(brg) * Math.sin(d) * Math.cos(lat1),
      Math.cos(d) - Math.sin(lat1) * sinLat2,
    );
  return { lat: lat2 * RAD, lon: normalizeLon(lon2 * RAD) };
}

/**
 * Точка на дуге большого круга при доле пути t (0 - a, 1 - b).
 * Сферическая интерполяция, а не линейная: линейная в районе полюсов и
 * антимеридиана даёт заметно кривую траекторию.
 */
export function slerpPoint(a: LatLon, b: LatLon, t: number): LatLon {
  if (t <= 0) return { lat: a.lat, lon: normalizeLon(a.lon) };
  if (t >= 1) return { lat: b.lat, lon: normalizeLon(b.lon) };

  const lat1 = toRad(a.lat);
  const lon1 = toRad(a.lon);
  const lat2 = toRad(b.lat);
  const lon2 = toRad(b.lon);

  const d = haversineKm(a, b) / EARTH_RADIUS_KM;
  // Совпадающие точки: sin(d) обращается в ноль, интерполировать нечего.
  if (d < 1e-12) return { lat: a.lat, lon: normalizeLon(a.lon) };

  const sinD = Math.sin(d);
  const A = Math.sin((1 - t) * d) / sinD;
  const B = Math.sin(t * d) / sinD;

  const x = A * Math.cos(lat1) * Math.cos(lon1) + B * Math.cos(lat2) * Math.cos(lon2);
  const y = A * Math.cos(lat1) * Math.sin(lon1) + B * Math.cos(lat2) * Math.sin(lon2);
  const z = A * Math.sin(lat1) + B * Math.sin(lat2);

  return {
    lat: Math.atan2(z, Math.sqrt(x * x + y * y)) * RAD,
    lon: normalizeLon(Math.atan2(y, x) * RAD),
  };
}
